/**
 * 钉钉文件选择器工具
 * 支持从钉钉文档与文件中选择Excel文件导入
 */

export interface DingTalkFile {
  fileId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  downloadUrl?: string;
  spaceId?: string;
  folderId?: string;
}

/**
 * 通过后端API获取钉钉文件列表
 * 注意：需要后端实现获取文件列表的接口
 */
export async function getDingTalkFileList(
  folderId: string = '0',
  fileTypes: string[] = ['xlsx', 'xls']
): Promise<DingTalkFile[]> {
  try {
    console.log('[钉钉文件] 开始获取文件列表...');
    
    // 调用后端API获取文件列表
    // 后端需要实现：GET /api/dingtalk/files?folderId=xxx&fileTypes=xlsx,xls
    const response = await fetch(
      `/api/dingtalk/files?folderId=${folderId}&fileTypes=${fileTypes.join(',')}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`获取文件列表失败: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    console.log(`[钉钉文件] 获取到 ${data.files?.length || 0} 个文件`);
    return data.files || [];
  } catch (error: any) {
    console.error('[钉钉文件] 获取文件列表失败:', error);
    throw new Error(`获取钉钉文件列表失败: ${error.message}`);
  }
}

/**
 * 下载钉钉文件并转换为File对象
 */
export async function downloadDingTalkFile(
  file: DingTalkFile
): Promise<File> {
  try {
    console.log(`[钉钉文件] 开始下载文件: ${file.fileName}`);
    
    // 如果没有下载链接，先获取
    let downloadUrl = file.downloadUrl;
    if (!downloadUrl) {
      // 调用后端API获取下载链接
      const response = await fetch(
        `/api/dingtalk/files/${file.fileId}/download-url`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`获取下载链接失败: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      
      downloadUrl = data.downloadUrl;
    }
    
    // 确保 downloadUrl 不为 undefined
    if (!downloadUrl) {
      throw new Error('无法获取文件下载链接');
    }
    
    // 下载文件
    const fileResponse = await fetch(downloadUrl);
    if (!fileResponse.ok) {
      throw new Error(`下载文件失败: ${fileResponse.statusText}`);
    }
    
    const blob = await fileResponse.blob();
    
    // 转换为File对象
    const fileObj = new File([blob], file.fileName, {
      type: blob.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    console.log(`[钉钉文件] 文件下载完成: ${file.fileName} (${fileObj.size} bytes)`);
    return fileObj;
  } catch (error: any) {
    console.error('[钉钉文件] 下载文件失败:', error);
    throw new Error(`下载钉钉文件失败: ${error.message}`);
  }
}

/**
 * 使用钉钉JSAPI选择文件（如果可用）
 * 这是更推荐的方式，直接使用钉钉的文件选择器
 */
export async function chooseDingTalkFileWithJSAPI(): Promise<DingTalkFile | null> {
  try {
    console.log('[钉钉文件] 尝试使用JSAPI选择文件...');
    
    // 检查Dingdocs SDK是否可用
    if (typeof (window as any).Dingdocs === 'undefined') {
      throw new Error('Dingdocs SDK未加载');
    }
    
    const Dingdocs = (window as any).Dingdocs;
    
    // 尝试使用文件选择API
    // 注意：具体的API名称可能需要根据实际SDK文档调整
    if (Dingdocs.base?.host?.chooseFile) {
      const result = await Dingdocs.base.host.chooseFile({
        accept: '.xlsx,.xls',
        multiple: false
      });
      
      if (result && result.files && result.files.length > 0) {
        const selectedFile = result.files[0];
        console.log('[钉钉文件] 文件选择成功:', selectedFile);
        
        return {
          fileId: selectedFile.fileId || selectedFile.id,
          fileName: selectedFile.fileName || selectedFile.name,
          fileSize: selectedFile.fileSize || selectedFile.size || 0,
          fileType: selectedFile.fileType || selectedFile.type || 'xlsx',
          downloadUrl: selectedFile.downloadUrl || selectedFile.url
        };
      }
    } else if (Dingdocs.script?.chooseFile) {
      // 尝试使用script API
      const result = await Dingdocs.script.chooseFile({
        accept: '.xlsx,.xls',
        multiple: false
      });
      
      if (result && result.files && result.files.length > 0) {
        const selectedFile = result.files[0];
        return {
          fileId: selectedFile.fileId || selectedFile.id,
          fileName: selectedFile.fileName || selectedFile.name,
          fileSize: selectedFile.fileSize || selectedFile.size || 0,
          fileType: selectedFile.fileType || selectedFile.type || 'xlsx',
          downloadUrl: selectedFile.downloadUrl || selectedFile.url
        };
      }
    } else {
      // 如果JSAPI不可用，返回null，使用后端API方案
      console.warn('[钉钉文件] JSAPI文件选择器不可用，将使用后端API方案');
      return null;
    }
    
    return null;
  } catch (error: any) {
    console.error('[钉钉文件] JSAPI选择文件失败:', error);
    // 失败时返回null，使用后端API方案
    return null;
  }
}

